import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import { generateStrategyPlan, listStrategyPlans, deleteStrategyPlan, listContractors, listNegotiations } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Target, Shield, DollarSign, Clock, AlertTriangle, ChevronRight,
  ChevronDown, Sparkles, Trash2, Eye, ArrowLeft, Plus, FileText,
  CheckCircle2, XCircle, Lightbulb, TrendingUp, Users, Layers
} from 'lucide-react';

const PRIORITY_OPTIONS = [
  { value: 'low', label: { pl: 'Niski', en: 'Low' }, color: 'text-blue-400 bg-blue-400/10' },
  { value: 'medium', label: { pl: 'Średni', en: 'Medium' }, color: 'text-amber-400 bg-amber-400/10' },
  { value: 'high', label: { pl: 'Wysoki', en: 'High' }, color: 'text-orange-400 bg-orange-400/10' },
  { value: 'critical', label: { pl: 'Krytyczny', en: 'Critical' }, color: 'text-red-400 bg-red-400/10' },
];

const APPROACH_LABELS = {
  relational: { pl: 'Relacyjne', en: 'Relational', icon: Users, color: 'text-blue-400' },
  data_driven: { pl: 'Oparte na danych', en: 'Data-driven', icon: TrendingUp, color: 'text-green-400' },
  hard: { pl: 'Twarde', en: 'Hard', icon: Shield, color: 'text-red-400' },
  standard: { pl: 'Standardowe', en: 'Standard', icon: Layers, color: 'text-amber-400' },
  education_first: { pl: 'Edukacyjne', en: 'Education-first', icon: Lightbulb, color: 'text-purple-400' },
};

export default function StrategyPlanner({ lang, tenantId }) {
  const [view, setView] = useState('list'); // list | create | detail
  const [plans, setPlans] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [negotiations, setNegotiations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});

  // Scenario form
  const [scenario, setScenario] = useState({
    goal: '', constraints: '', red_lines: '', budget: '',
    timeline: '', context: '', priority: 'medium'
  });
  const [contractorId, setContractorId] = useState('');
  const [negotiationId, setNegotiationId] = useState('');

  useEffect(() => { loadData(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    setLoading(true);
    try {
      const [p, c, n] = await Promise.all([
        listStrategyPlans(tenantId),
        listContractors(tenantId),
        listNegotiations(tenantId)
      ]);
      setPlans(p);
      setContractors(c);
      setNegotiations(n);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  function resetForm() {
    setScenario({ goal: '', constraints: '', red_lines: '', budget: '', timeline: '', context: '', priority: 'medium' });
    setContractorId('');
    setNegotiationId('');
  }

  async function handleGenerate() {
    if (!scenario.goal.trim()) {
      toast.error(lang === 'pl' ? 'Podaj cel negocjacji' : 'Provide a negotiation goal');
      return;
    }
    setGenerating(true);
    try {
      const result = await generateStrategyPlan({
        scenario,
        contractor_id: contractorId || undefined,
        negotiation_id: negotiationId || undefined
      }, tenantId);
      toast.success(lang === 'pl' ? 'Plan wygenerowany!' : 'Plan generated!');
      setSelectedPlan(result);
      setView('detail');
      resetForm();
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd generowania planu' : 'Plan generation failed');
    }
    setGenerating(false);
  }

  async function handleDelete(planId) {
    try {
      await deleteStrategyPlan(planId);
      toast.success(lang === 'pl' ? 'Plan usunięty' : 'Plan deleted');
      setPlans(prev => prev.filter(p => p.id !== planId));
      if (selectedPlan?.id === planId) {
        setView('list');
        setSelectedPlan(null);
      }
    } catch (err) {
      toast.error(lang === 'pl' ? 'Błąd usuwania' : 'Delete failed');
    }
  }

  function togglePhase(idx) {
    setExpandedPhases(prev => ({ ...prev, [idx]: !prev[idx] }));
  }

  function openPlan(plan) {
    setSelectedPlan(plan);
    setExpandedPhases({});
    setView('detail');
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      </div>
    );
  }

  // ===== PLAN DETAIL VIEW =====
  if (view === 'detail' && selectedPlan) {
    const plan = selectedPlan.plan || {};
    const approach = APPROACH_LABELS[plan.recommended_approach] || APPROACH_LABELS.standard;
    const ApproachIcon = approach.icon;

    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedPlan(null); }}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {lang === 'pl' ? 'Wróć' : 'Back'}
          </Button>
          <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {plan.plan_name || (lang === 'pl' ? 'Plan strategiczny' : 'Strategy Plan')}
          </h1>
          {plan.status === 'failed' && (
            <Badge variant="destructive">{lang === 'pl' ? 'Nie udało się' : 'Failed'}</Badge>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <ApproachIcon className={`w-6 h-6 mx-auto mb-1 ${approach.color}`} />
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Podejście' : 'Approach'}</p>
              <p className="text-sm font-semibold">{approach[lang] || approach.en}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Target className="w-6 h-6 mx-auto mb-1 text-primary" />
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Szacowane tury' : 'Est. Turns'}</p>
              <p className="text-sm font-semibold">{plan.estimated_turns || '?'}</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <TrendingUp className="w-6 h-6 mx-auto mb-1 text-green-400" />
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Szansa sukcesu' : 'Success Prob.'}</p>
              <p className="text-sm font-semibold">{Math.round((plan.success_probability || 0) * 100)}%</p>
            </CardContent>
          </Card>
          <Card className="border border-border">
            <CardContent className="p-4 text-center">
              <Layers className="w-6 h-6 mx-auto mb-1 text-amber-400" />
              <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Fazy' : 'Phases'}</p>
              <p className="text-sm font-semibold">{(plan.phases || []).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Executive Summary */}
        {plan.executive_summary && (
          <Card className="border border-border">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground mb-1">{lang === 'pl' ? 'Podsumowanie strategii' : 'Strategy Summary'}</p>
              <p className="text-sm">{plan.executive_summary}</p>
            </CardContent>
          </Card>
        )}

        {/* Phases */}
        {plan.phases && plan.phases.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Layers className="w-4 h-4 text-primary" />
                {lang === 'pl' ? 'Fazy negocjacji' : 'Negotiation Phases'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 space-y-2">
              {plan.phases.map((phase, i) => {
                const isExpanded = expandedPhases[i];
                return (
                  <div key={i} className="border border-border rounded-xl overflow-hidden">
                    <button
                      onClick={() => togglePhase(i)}
                      className="w-full flex items-center gap-3 p-4 text-left hover:bg-muted/30 transition-colors"
                    >
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-sm font-bold flex-shrink-0">
                        {phase.phase_number || i + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{phase.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{phase.goal}</p>
                      </div>
                      <Badge variant="secondary" className="text-xs flex-shrink-0">{phase.duration}</Badge>
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    </button>
                    {isExpanded && (
                      <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                        {phase.tactics && phase.tactics.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              {lang === 'pl' ? 'Taktyki' : 'Tactics'}
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                              {phase.tactics.map((tactic, j) => (
                                <Badge key={j} variant="secondary" className="text-xs">{tactic}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {phase.what_to_say && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              {lang === 'pl' ? 'Co powiedzieć' : 'What to say'}
                            </p>
                            <p className="text-sm bg-muted/30 rounded-lg p-3 italic">"{phase.what_to_say}"</p>
                          </div>
                        )}
                        {phase.what_to_watch && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">
                              {lang === 'pl' ? 'Na co uważać' : 'What to watch'}
                            </p>
                            <p className="text-sm">{phase.what_to_watch}</p>
                          </div>
                        )}
                        {phase.success_criteria && (
                          <div className="flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">{lang === 'pl' ? 'Kryterium sukcesu' : 'Success criteria'}</p>
                              <p className="text-sm">{phase.success_criteria}</p>
                            </div>
                          </div>
                        )}
                        {phase.fallback && (
                          <div className="flex items-start gap-2">
                            <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">{lang === 'pl' ? 'Plan B' : 'Fallback'}</p>
                              <p className="text-sm">{phase.fallback}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Concession Strategy */}
          {plan.concession_strategy && Object.keys(plan.concession_strategy).length > 0 && (
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-400" />
                  {lang === 'pl' ? 'Strategia ustępstw' : 'Concession Strategy'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                {plan.concession_strategy.opening_position && (
                  <div>
                    <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Pozycja startowa' : 'Opening position'}</p>
                    <p className="text-sm font-medium">{plan.concession_strategy.opening_position}</p>
                  </div>
                )}
                {plan.concession_strategy.ideal_outcome && (
                  <div>
                    <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Idealny wynik' : 'Ideal outcome'}</p>
                    <p className="text-sm">{plan.concession_strategy.ideal_outcome}</p>
                  </div>
                )}
                {plan.concession_strategy.acceptable_outcome && (
                  <div>
                    <p className="text-xs text-muted-foreground">{lang === 'pl' ? 'Akceptowalny wynik' : 'Acceptable outcome'}</p>
                    <p className="text-sm">{plan.concession_strategy.acceptable_outcome}</p>
                  </div>
                )}
                {plan.concession_strategy.walk_away_point && (
                  <div className="flex items-start gap-2 p-2 rounded-lg bg-red-400/5 border border-red-400/20">
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-red-400">{lang === 'pl' ? 'Punkt odejścia' : 'Walk-away point'}</p>
                      <p className="text-sm">{plan.concession_strategy.walk_away_point}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Risk Analysis */}
          {plan.risk_analysis && plan.risk_analysis.length > 0 && (
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  {lang === 'pl' ? 'Analiza ryzyk' : 'Risk Analysis'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0 space-y-2">
                {plan.risk_analysis.map((risk, i) => (
                  <div key={i} className="p-3 rounded-lg bg-muted/30 border border-border">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge variant="secondary" className={`text-xs ${
                        risk.probability === 'high' ? 'bg-red-400/10 text-red-400'
                        : risk.probability === 'medium' ? 'bg-amber-400/10 text-amber-400'
                        : 'bg-blue-400/10 text-blue-400'
                      }`}>{risk.probability}</Badge>
                      <p className="text-sm font-medium">{risk.risk}</p>
                    </div>
                    <p className="text-xs text-muted-foreground">{risk.mitigation}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Key Arguments & Questions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {plan.key_arguments && plan.key_arguments.length > 0 && (
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold">{lang === 'pl' ? 'Kluczowe argumenty' : 'Key Arguments'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-1.5">
                  {plan.key_arguments.map((arg, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 flex-shrink-0" />
                      {arg}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {plan.questions_to_ask && plan.questions_to_ask.length > 0 && (
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold">{lang === 'pl' ? 'Pytania do zadania' : 'Questions to Ask'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-1.5">
                  {plan.questions_to_ask.map((q, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <Lightbulb className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                      {q}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
          {plan.warnings && plan.warnings.length > 0 && (
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold">{lang === 'pl' ? 'Ostrzeżenia' : 'Warnings'}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ul className="space-y-1.5">
                  {plan.warnings.map((w, i) => (
                    <li key={i} className="text-sm flex items-start gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                      {w}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Milestones */}
        {plan.milestones && plan.milestones.length > 0 && (
          <Card className="border border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                {lang === 'pl' ? 'Kamienie milowe' : 'Milestones'}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {plan.milestones.map((ms, i) => (
                  <React.Fragment key={i}>
                    <div className="flex-shrink-0 p-3 rounded-xl bg-muted/30 border border-border min-w-[180px]">
                      <p className="text-sm font-medium">{ms.name}</p>
                      <p className="text-xs text-muted-foreground">{ms.description}</p>
                      <Badge variant="secondary" className="text-xs mt-1">
                        {lang === 'pl' ? `Tura ${ms.target_turn}` : `Turn ${ms.target_turn}`}
                      </Badge>
                    </div>
                    {i < plan.milestones.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ===== CREATE VIEW =====
  if (view === 'create') {
    return (
      <div className="space-y-6 max-w-2xl">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setView('list')}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            {lang === 'pl' ? 'Wróć' : 'Back'}
          </Button>
          <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Nowy plan strategiczny' : 'New Strategy Plan'}
          </h1>
        </div>

        <Card className="border border-border">
          <CardContent className="p-6 space-y-5">
            {/* Goal */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Target className="w-3 h-3" />
                {lang === 'pl' ? 'Cel główny negocjacji *' : 'Main negotiation goal *'}
              </Label>
              <Textarea
                value={scenario.goal}
                onChange={e => setScenario(s => ({ ...s, goal: e.target.value }))}
                className="mt-1"
                rows={2}
                placeholder={lang === 'pl'
                  ? 'np. Wynegocjować rabat 15% na dostawy kwartalne...'
                  : 'e.g. Negotiate 15% discount on quarterly deliveries...'}
              />
            </div>

            {/* Constraints */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Shield className="w-3 h-3" />
                {lang === 'pl' ? 'Ograniczenia' : 'Constraints'}
              </Label>
              <Textarea
                value={scenario.constraints}
                onChange={e => setScenario(s => ({ ...s, constraints: e.target.value }))}
                className="mt-1"
                rows={2}
                placeholder={lang === 'pl'
                  ? 'np. Nie możemy zmienić dostawcy w tym kwartale...'
                  : 'e.g. Cannot switch suppliers this quarter...'}
              />
            </div>

            {/* Red Lines */}
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3 text-red-400" />
                {lang === 'pl' ? 'Czerwone linie (nie przekraczaj)' : 'Red lines (do not cross)'}
              </Label>
              <Textarea
                value={scenario.red_lines}
                onChange={e => setScenario(s => ({ ...s, red_lines: e.target.value }))}
                className="mt-1"
                rows={2}
                placeholder={lang === 'pl'
                  ? 'np. Nie akceptujemy płatności powyżej 60 dni...'
                  : 'e.g. Do not accept payment terms above 60 days...'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Budget */}
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <DollarSign className="w-3 h-3" />
                  {lang === 'pl' ? 'Budżet' : 'Budget'}
                </Label>
                <Input
                  value={scenario.budget}
                  onChange={e => setScenario(s => ({ ...s, budget: e.target.value }))}
                  className="mt-1"
                  placeholder={lang === 'pl' ? 'np. 50 000 PLN' : 'e.g. $50,000'}
                />
              </div>

              {/* Timeline */}
              <div>
                <Label className="text-xs flex items-center gap-1.5">
                  <Clock className="w-3 h-3" />
                  {lang === 'pl' ? 'Deadline' : 'Timeline'}
                </Label>
                <Input
                  value={scenario.timeline}
                  onChange={e => setScenario(s => ({ ...s, timeline: e.target.value }))}
                  className="mt-1"
                  placeholder={lang === 'pl' ? 'np. do końca marca' : 'e.g. end of March'}
                />
              </div>
            </div>

            {/* Context */}
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Dodatkowy kontekst' : 'Additional context'}</Label>
              <Textarea
                value={scenario.context}
                onChange={e => setScenario(s => ({ ...s, context: e.target.value }))}
                className="mt-1"
                rows={2}
                placeholder={lang === 'pl'
                  ? 'np. To nasz stały dostawca od 3 lat, ale jakość ostatnio spadła...'
                  : 'e.g. Long-term supplier for 3 years, but quality has recently declined...'}
              />
            </div>

            {/* Priority */}
            <div>
              <Label className="text-xs">{lang === 'pl' ? 'Priorytet' : 'Priority'}</Label>
              <div className="flex gap-2 mt-1">
                {PRIORITY_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setScenario(s => ({ ...s, priority: opt.value }))}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border-2 transition-all ${
                      scenario.priority === opt.value
                        ? `${opt.color} border-current`
                        : 'border-border text-muted-foreground hover:border-border/80'
                    }`}
                  >
                    {opt.label[lang] || opt.label.en}
                  </button>
                ))}
              </div>
            </div>

            {/* Contractor / Negotiation Selection */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Kontrahent (opcjonalnie)' : 'Contractor (optional)'}</Label>
                <select
                  value={contractorId}
                  onChange={e => setContractorId(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{lang === 'pl' ? '— wybierz —' : '— select —'}</option>
                  {contractors.map(c => (
                    <option key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Negocjacja (opcjonalnie)' : 'Negotiation (optional)'}</Label>
                <select
                  value={negotiationId}
                  onChange={e => setNegotiationId(e.target.value)}
                  className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
                >
                  <option value="">{lang === 'pl' ? '— wybierz —' : '— select —'}</option>
                  {negotiations.map(n => (
                    <option key={n.id} value={n.id}>{n.title}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerate}
              disabled={generating || !scenario.goal.trim()}
              className="w-full gap-2"
              size="lg"
            >
              <Sparkles className="w-4 h-4" />
              {generating
                ? (lang === 'pl' ? 'Generowanie planu...' : 'Generating plan...')
                : (lang === 'pl' ? 'Wygeneruj plan strategiczny' : 'Generate Strategy Plan')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ===== LIST VIEW =====
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Planer strategii' : 'Strategy Planner'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl'
              ? 'Definiuj scenariusze i generuj automatyczne plany działania'
              : 'Define scenarios and generate automatic action plans'}
          </p>
        </div>
        <Button onClick={() => setView('create')} className="gap-1.5">
          <Plus className="w-4 h-4" />
          {lang === 'pl' ? 'Nowy plan' : 'New Plan'}
        </Button>
      </div>

      {plans.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="py-16 text-center">
            <Target className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              {lang === 'pl'
                ? 'Nie masz jeszcze żadnych planów strategicznych.'
                : 'You don\'t have any strategy plans yet.'}
            </p>
            <Button onClick={() => setView('create')} className="gap-1.5">
              <Sparkles className="w-4 h-4" />
              {lang === 'pl' ? 'Stwórz pierwszy plan' : 'Create first plan'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {plans.map(p => {
            const plan = p.plan || {};
            const approach = APPROACH_LABELS[plan.recommended_approach] || APPROACH_LABELS.standard;
            const ApproachIcon = approach.icon;
            const contractor = contractors.find(c => c.id === p.contractor_id);

            return (
              <Card
                key={p.id}
                className="border border-border hover:border-border/80 transition-colors cursor-pointer group"
                onClick={() => openPlan(p)}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{plan.plan_name || (lang === 'pl' ? 'Plan' : 'Plan')}</p>
                      {contractor && (
                        <p className="text-xs text-muted-foreground">{contractor.name}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <Button
                        variant="ghost" size="sm"
                        className="opacity-0 group-hover:opacity-100 h-7 w-7 p-0"
                        onClick={e => { e.stopPropagation(); handleDelete(p.id); }}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-400" />
                      </Button>
                    </div>
                  </div>
                  {plan.executive_summary && (
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{plan.executive_summary}</p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className={`text-xs ${approach.color}`}>
                      <ApproachIcon className="w-3 h-3 mr-1" />
                      {approach[lang] || approach.en}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      {(plan.phases || []).length} {lang === 'pl' ? 'faz' : 'phases'}
                    </Badge>
                    {plan.success_probability && (
                      <Badge variant="secondary" className="text-xs text-green-400">
                        {Math.round(plan.success_probability * 100)}%
                      </Badge>
                    )}
                    {plan.status === 'failed' && (
                      <Badge variant="destructive" className="text-xs">{lang === 'pl' ? 'Błąd' : 'Failed'}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground/60 mt-3">
                    {new Date(p.created_at).toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-US')}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
